import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { useAppSelector } from '@common/store';
import { UserState } from '@common/types/store';
import analytics from '@common/utils/analytics';

const pathsToIgnore = ['/', '/login'];

export const PAGE_VIEW_DELAY_MS = 300;

function usePageViewTracker() {
  const { pathname } = useLocation();
  const previousPath = useRef<string | undefined>(undefined);
  const userState = useAppSelector((state) => state.main.userState);

  // A new login starts a new session, so its first page counts even if it was the last one seen
  useEffect(() => {
    if (userState === UserState.LoggedIn) {
      previousPath.current = undefined;
    }
  }, [userState]);

  useEffect(() => {
    // PostHog may still hold an expired session's user until the current user has been checked
    if (userState === UserState.Unknown || process.env.TRACK_PAGE_VIEWS !== 'true') {
      return undefined;
    }
    // Cancelled when the page changes first, e.g. a redirect to login, so it isn't counted
    const timeout = setTimeout(() => {
      if (pathname !== previousPath.current && !pathsToIgnore.includes(pathname)) {
        previousPath.current = pathname;
        analytics.captureEvent('$pageview', { $current_url: window.location.href });
      }
    }, PAGE_VIEW_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [pathname, userState]);
}

export default usePageViewTracker;
